import { cx } from '@/lib/cx';

/**
 * The leading mark on a chosen row, and the leading SPACER on the pinned rows
 * that choose nothing — one component so the two can never drift apart and
 * leave the list ragged.
 *
 * ⚠️ **ONE COPY, SHARED BY `MultiCombobox`, `Combobox` AND `ColumnFilter`** —
 * owner's instruction 2026-09-13, *"checkboxes before the option and the tick
 * from end removed, do this across the app"*. It lived inside `MultiCombobox`
 * until the single-select needed it too; three local copies would agree the day
 * they were written and drift on the first correction, and what would drift is
 * the one thing this component exists to get right — where the box sits against
 * a two-line row.
 *
 * ⛔ **A REAL `<input type="checkbox">`, DELIBERATELY NOT A DRAWN BOX.** It
 * inherits `accent-color` and so renders identically to every other checkbox in
 * the app in BOTH themes; a hand-drawn SVG would be a second visual language to
 * keep in step with `Checkbox`. The classes are that primitive's own.
 *
 * ⛔ **AND IT IS NON-INTERACTIVE, WHICH IS THE WHOLE REASON `Checkbox` ITSELF
 * CANNOT BE MOUNTED HERE.** These rows are `role="option"` inside a
 * `role="listbox"` driven by `aria-activedescendant`: one Tab stop, arrow-key
 * navigation, and `onPointerDown` + `preventDefault()` so the input's blur
 * cannot close the popover before the toggle runs. A focusable checkbox inside
 * an option would add a second tab stop, break the activedescendant model and
 * swallow that pointer event. So: `readOnly` (never `defaultChecked`, which
 * would make it uncontrolled and let it drift from `values`), `tabIndex={-1}`,
 * `aria-hidden` — the row's own `aria-selected` is what a screen reader
 * announces, and a second announcement of the same fact is noise — and
 * `pointer-events-none` so every click lands on the row.
 *
 * ⛔ **THAT NON-INTERACTIVITY IS ALSO WHAT MAKES A SQUARE BOX HONEST ON A
 * SINGLE-SELECT.** A checkbox on a list where ticking one row unticks another
 * would be a false affordance if it were the accessible representation — but
 * nothing announces a checkbox here at all: the tree reads `listbox` /
 * `option` / `aria-selected`, exactly as it did when the mark was a trailing
 * tick. The shape is a visual convention shared with the multi-select; the
 * semantics come from the row. **So do not "correct" the single-select to a
 * radio** — that would put two selection languages in one popover, which is
 * the inconsistency this component was shared to remove.
 */
export function RowCheckbox({ checked }: { checked?: boolean }) {
  /*
   * ⚠️ **`self-start mt-0.5`, NOT the row's own `items-center` — and on a
   * ONE-LINE row the two are pixel-identical, which is why this is easy to
   * "simplify" away.** An option carrying a `hint` is a TWO-LINE block, and
   * many mounts pass hints (a product code, a city's country, "No country
   * recorded"). Centred against a 36px two-line block the box lands at ~10px,
   * straddling the gap between the label and its hint rather than marking
   * either. Pinned to the first line it sits at 2px — exactly where
   * `items-center` puts it on a 20px single-line row, and exactly where
   * `Checkbox`'s own `mt-0.5` puts it in the filter popover.
   */
  const className = 'mt-0.5 h-4 w-4 shrink-0 self-start';
  // The spacer case. Rendering an invisible box rather than a bare `w-4` keeps
  // the two paths one width by construction.
  if (checked === undefined) return <span aria-hidden="true" className={className} />;
  return (
    <input
      type="checkbox"
      checked={checked}
      readOnly
      tabIndex={-1}
      aria-hidden="true"
      className={cx(className, 'pointer-events-none rounded-xs border-border accent-accent-brand')}
    />
  );
}
